import React, { useEffect, useState } from "react";
import NxtbnModal from "../Modal";
import { InputField, NXForm } from "../common";
import SelectStyled from "../Select";
import useApi from "../../api";
import { toast } from "react-toastify";
import enumChoice, { makeEnumFriendly } from "../../enum";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/rootReducer";
import { fetchWarehouse } from "../../redux/warehosueSlice";

interface ReturnRequestFormProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItems: number[];
  lineItems: any;
  order_id: any;
}

function ReturnRequestForm({
  isOpen,
  onClose,
  selectedItems,
  lineItems,
  order_id,
}: ReturnRequestFormProps) {
  const [formData, setFormData] = useState<any>({
    line_items: [],  // Initialize with selected items
    reason: "",
    reason_details: "",
  });
  const [errorData, setErrorData] = useState<any>();

  const api = useApi();
  const warehouse = useSelector((state: RootState) => state.warehouse);
  const dispatch = useDispatch();

  const fetchWarehouseData = () => {
    if (!warehouse.results.length){
      dispatch(fetchWarehouse() as any);
    }
  };

  useEffect(() => {
    fetchWarehouseData();
  }, []);

  useEffect(() => {
    const data = {
        reason: "",
        order: order_id,
        line_items: lineItems.filter((item: any) => selectedItems.includes(item.dbId))
            .map((item: any) => ({
                order_line_item: item.dbId,
                name: item?.variant?.displayName, // will not used in payload process
                quantity: item.quantity,
                max_quantity: item.quantity, // will not count in payload
            })),
    };
    setFormData(data);
  }, [selectedItems]);

  const handleChange = (e: any, index: number) => {
    const { name, value } = e.target;
    setFormData((prev: any) => {
      const updatedItems = [...prev.line_items];
      updatedItems[index][name] = value;
      return { ...prev, items: updatedItems };
    });
  };

  const handleReasonChange = (e: any) => {
    setFormData((prev: any) => ({
      ...prev,
      reason: e.value,
    }));
  };


  const handleDestinationChange = (e: any) => {
    setFormData((prev: any) => {
      const updatedItems = prev.line_items.map((item: any) => ({
        ...item,
        destination: e.value,
      }));
      return { ...prev, line_items: updatedItems };
    });
  };



  const handleReasonDetailsChange = (e: any) => {
    setFormData((prev: any) => ({
      ...prev,
      reason_details: e.target.value,
    }));
  }

  const onSubmit = (e: any) => {
    e.preventDefault();

    api.createReturnRequest(formData).then(
      (response: any) => {
        toast.success("Return request successfully created.");
        onClose();
      },
      (error: any) => setErrorData(error.response.data)
    );
  };

  return (
    <NxtbnModal size="sm" isOpen={isOpen} onClose={onClose}>
      <div className="p-4">
        <h2 className="border-b pb-3">Return Request</h2>
        <NXForm
          className="mt-6"
          onSubmit={onSubmit}
          nonFieldError={errorData}
          showErrorPopup={true}
        >

          {errorData?.line_items?.map((error: string, index: number) => (
            <div key={index} className="text-red-500 text-sm">
              {error}
            </div>
          ))}

          {/* List of Items with Quantity Adjusters */}
          {formData.line_items.map((item: any, index: number) => (
            <div key={item.id} className="flex items-center mb-4">
                <p className="w-1/2">#ID{item.order_line_item}</p>
              <p className="w-1/2">{item.name}</p>
              <div className="flex items-center">
                <InputField
                  type="number"
                  name="quantity"
                  value={item.quantity}
                  min="1"
                  max={item.max_quantity}
                  onChange={(e) => handleChange(e, index)}
                  className="text-center w-12 border border-gray-300"
                />
              </div>
            </div>
          ))}

          <div className="mt-4">
            <label
              htmlFor="reason"
              className="block text-sm font-medium text-gray-700"
            >
              Return Reason
            </label>
            <SelectStyled
              name="reason"
              onChange={handleReasonChange}
              options={enumChoice.returnReasons}
              menuPortalTarget={document.body}
                customStyles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
            }}              
            />
          </div>

          <div className="mt-4">
            <label
              htmlFor="destination"
              className="block text-sm font-medium text-gray-700"
            >
              Destination
            </label>
            <SelectStyled
              name="destination"
              onChange={handleDestinationChange}
              options={makeEnumFriendly(warehouse.results)}
              menuPortalTarget={document.body}
                customStyles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
            }}              
            />
          </div>

          {/* Reason Details Field (conditionally rendered) */}
          {formData.reason === enumChoice.returnReasons.toKV().OTHER.value && (
            <div className="mt-4">
              <label
                htmlFor="reason_details"
                className="block text-sm font-medium text-gray-700"
              >
                Reason Details
              </label>
              <textarea
                name="reason_details"
                value={formData.reason_details || ""}
                onChange={handleReasonDetailsChange}
                placeholder="Please specify your reason"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          )}

          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={onClose}
              className="mr-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Submit Return
            </button>
          </div>
        </NXForm>
      </div>
    </NxtbnModal>
  );
}

export default ReturnRequestForm;